import React, { useState, useMemo, useEffect } from 'react';
import { BookOpen, RefreshCw } from 'lucide-react';
import SearchBar from './components/SearchBar';
import FilterBar from './components/FilterBar';
import BookList from './components/BookList';
import ReadingListPanel from './components/ReadingListPanel';
import { useGoogleSheets } from './hooks/useGoogleSheets';
import { useReadingList } from './hooks/useReadingList';
import { UI_TEXT } from './utils/constants';

function App() {
  const { books, loading, error, refetch } = useGoogleSheets();
  const { items: readingListItems, toggle, remove, clear, isSaved } = useReadingList();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const _initialParams = new URLSearchParams(window.location.search);
  const [searchQuery, setSearchQuery] = useState(_initialParams.get('q') ?? '');
  const [selectedGenre, setSelectedGenre] = useState(_initialParams.get('genre') ?? 'Усі');
  const [selectedAge, setSelectedAge] = useState(_initialParams.get('age') ?? 'Усі');
  const [showOnlyNew, setShowOnlyNew] = useState(_initialParams.get('new') === '1');

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedGenre !== 'Усі') params.set('genre', selectedGenre);
    if (selectedAge !== 'Усі') params.set('age', selectedAge);
    if (showOnlyNew) params.set('new', '1');
    const qs = params.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [searchQuery, selectedGenre, selectedAge, showOnlyNew]);

  // Extract unique genres from books
  const genres = useMemo(() => {
    const uniqueGenres = [...new Set(books.map(book => book.genre))];
    return uniqueGenres.filter(genre => genre !== '');
  }, [books]);

  const hasNewBooks = useMemo(() => books.some(book => book.isNew), [books]);

  if (loading) {
    return (
      <div className="min-h-screen page-bg flex items-center justify-center">
        <div className="text-center bg-white/75 backdrop-blur-sm rounded-2xl shadow-lg px-14 py-12">
          <RefreshCw className="h-12 w-12 text-primary animate-spin mx-auto mb-5" />
          <p className="text-gray-600 text-lg font-medium">{UI_TEXT.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen page-bg flex items-center justify-center">
        <div className="text-center bg-white/75 backdrop-blur-sm rounded-2xl shadow-lg px-14 py-12">
          <p className="text-red-500 text-lg mb-6 font-medium">{error}</p>
          <button
            onClick={refetch}
            className="px-8 py-3 bg-primary-container text-on-primary-container rounded-full font-semibold hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 transition-all duration-150"
          >
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen page-bg py-3 px-2 sm:py-8 sm:px-4 lg:py-10">
      {/* Emerald overlay on background image */}
      <div className="fixed inset-0 bg-emerald-900/30 pointer-events-none" aria-hidden="true" />

      {/* Frosted glass content panel */}
      <div className="relative z-10 max-w-7xl mx-auto bg-white/85 backdrop-blur-xl rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl border border-white/50 overflow-hidden">

      {/* Header */}
      <header className="header-gradient text-white relative overflow-hidden">
        {/* Dot grid overlay */}
        <div className="header-dots absolute inset-0 pointer-events-none" aria-hidden="true" />
        {/* Bottom glow line */}
        <div className="header-glow-line absolute bottom-0 left-0 right-0 h-px pointer-events-none" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-9 relative z-10">
          <div className="flex items-center gap-3 sm:gap-5">
            {/* Icon with layered glow */}
            <div className="relative flex-shrink-0">
              <div
                className="icon-glow absolute inset-0 rounded-2xl pointer-events-none"
                style={{ background: 'linear-gradient(135deg, #a8cec6, #53615e)', filter: 'blur(10px)', opacity: 0.7 }}
                aria-hidden="true"
              />
              <div className="relative bg-[#a8cec6]/15 backdrop-blur-sm border border-[#a8cec6]/30 rounded-2xl p-2 sm:p-3 shadow-lg">
                <BookOpen className="h-7 w-7 sm:h-9 sm:w-9 text-[#ddeee9]" aria-hidden="true" />
              </div>
            </div>

            {/* Title + subtitle */}
            <div className="flex-1 min-w-0">
              <h1
                className="text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight sm:leading-none"
                style={{
                  background: 'linear-gradient(120deg, #ffffff 0%, #ddeee9 55%, #a8cec6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.02em',
                }}
              >
                {UI_TEXT.appTitle}
              </h1>
              <p className="text-[#ddeee9]/90 text-xs sm:text-sm mt-1.5 font-medium leading-snug">
                {UI_TEXT.appSubtitle}
              </p>
            </div>

            {/* Reading list button */}
            {readingListItems.length > 0 && (
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-2 px-3 py-2 sm:px-4 rounded-full bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold transition-all flex-shrink-0 min-w-[44px] min-h-[44px] shadow-md"
                aria-label={UI_TEXT.myList}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-white flex-shrink-0" aria-hidden="true">
                  <path d="M5 3a2 2 0 0 0-2 2v16l9-4 9 4V5a2 2 0 0 0-2-2H5z" />
                </svg>
                <span className="hidden xs:inline sm:inline">{UI_TEXT.myList}</span>
                <span className="bg-white text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {readingListItems.length}
                </span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="lg:flex lg:gap-6 lg:items-start">
          <div className="flex-1 min-w-0">
            {/* Search + Filter panel */}
            <div className="rounded-2xl p-6 mb-8 bg-surface-low">
              <div className="mb-4">
                <SearchBar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
              </div>
              <FilterBar
                selectedGenre={selectedGenre}
                setSelectedGenre={setSelectedGenre}
                selectedAge={selectedAge}
                setSelectedAge={setSelectedAge}
                genres={genres}
                showOnlyNew={showOnlyNew}
                setShowOnlyNew={setShowOnlyNew}
                hasNewBooks={hasNewBooks}
              />
            </div>

            {/* Book List */}
            <BookList
              books={books}
              searchQuery={searchQuery}
              selectedGenre={selectedGenre}
              selectedAge={selectedAge}
              showOnlyNew={showOnlyNew}
              isSaved={isSaved}
              onToggleSave={toggle}
            />
          </div>

          {/* Desktop side panel — hidden on mobile, shown lg+ */}
          {readingListItems.length > 0 && (
            <div className="hidden lg:block">
              <ReadingListPanel
                items={readingListItems}
                onRemove={remove}
                onClear={clear}
              />
            </div>
          )}
        </div>
      </main>

      {/* Mobile drawer — visible below lg, slides in from right */}
      {drawerOpen && readingListItems.length > 0 && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer panel */}
          <div className="absolute right-0 top-0 h-full w-80 max-w-[90vw] overflow-y-auto shadow-2xl bg-white">
            <ReadingListPanel
              items={readingListItems}
              onRemove={remove}
              onClear={clear}
              onClose={() => setDrawerOpen(false)}
              isDrawer
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-surface-low mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-400 text-sm">
            © 2026 {UI_TEXT.appSubtitle}
          </p>
        </div>
      </footer>

      </div>{/* end frosted glass panel */}
    </div>
  );
}

export default App;
